import { PromptRefinementAngle } from '@api/api/prompt-refinement';
import { Button } from '@design/components/button/Button';
import { Card } from '@design/components/card/Card';
import { useRows } from '@terminal/components/chat';
import { RowSelectionCheckbox } from '@terminal/components/row-selection-checkbox/RowSelectionCheckbox';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './PromptAngleCard.module.scss';
import { getLensColorVar } from './utils/getLensColorVar';

export interface PromptAngleCardProps {
  angle: PromptRefinementAngle;
  isOpen: boolean;
  isSelected: boolean;
  showSelection: boolean;
  onSelectPrompt: (prompt: string) => void;
  onSelectionChange: (isSelected: boolean) => void;
  onSuggestionEdited?: () => void;
  onPromptChange?: (prompt: string) => void;
}

export const PromptAngleCard = ({
  angle,
  isOpen,
  isSelected,
  showSelection,
  onSelectPrompt,
  onSelectionChange,
  onSuggestionEdited,
  onPromptChange,
}: PromptAngleCardProps) => {
  const { t } = useTranslation('chat');
  const [isEditing, setIsEditing] = useState(false);
  const [displayedPrompt, setDisplayedPrompt] = useState(angle.prompt);
  const [draftPrompt, setDraftPrompt] = useState(angle.prompt);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  // useRows' measure effect only fires when its `value` changes, so passing an
  // empty string while not editing forces it to re-measure when the textarea
  // mounts on edit-mode entry. textAreaLineHeight matches typography-v2-body-sm.
  const { rows, updateRows } = useRows({
    value: isEditing ? draftPrompt : '',
    textAreaRef: editInputRef,
    textAreaLineHeight: 20,
    compactMode: true,
  });

  useEffect(() => {
    setIsEditing(false);
    setDisplayedPrompt(angle.prompt);
    setDraftPrompt(angle.prompt);
  }, [isOpen, angle.prompt]);

  const setEditInputRef = (el: HTMLTextAreaElement | null) => {
    editInputRef.current = el;

    if (!el) {
      return;
    }

    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  };

  const handleStartEdit = () => {
    setDraftPrompt(displayedPrompt);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setDraftPrompt(displayedPrompt);
    setIsEditing(false);
  };

  const handleApplyEdit = () => {
    const trimmed = draftPrompt.trim();

    if (!trimmed || trimmed === displayedPrompt.trim()) {
      return;
    }

    setDisplayedPrompt(trimmed);
    setDraftPrompt(trimmed);
    setIsEditing(false);
    onSuggestionEdited?.();
    onPromptChange?.(trimmed);
  };

  const trimmedDraft = draftPrompt.trim();
  const isUpdateDisabled =
    !trimmedDraft || trimmedDraft === displayedPrompt.trim();
  const selectionLabel = t('chat:promptRefinement.selectPrompt');
  const selectionName = `prompt-refinement-${angle.lens}-${angle.label}`
    .toLowerCase()
    .replace(/\s+/g, '-');

  const handleSelectionChange = (
    _: string,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    onSelectionChange(event.target.checked);
  };

  return (
    <Card className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <p
            className={styles.cardEyebrow}
            style={{ color: getLensColorVar(angle.lens) }}
          >
            {angle.lens}
          </p>
          <h5 className={styles.cardTitle}>{angle.label}</h5>
        </div>
        <div className={styles.cardMeta}>
          <span className={styles.cardMetaText}>{angle.depth}</span>
          {showSelection && (
            <RowSelectionCheckbox
              name={selectionName}
              checked={isSelected}
              onChange={handleSelectionChange}
              aria-label={selectionLabel}
              size="small"
            />
          )}
        </div>
      </div>
      {isEditing ? (
        <textarea
          ref={setEditInputRef}
          className={styles.editInput}
          value={draftPrompt}
          onChange={e => {
            updateRows(e);
            setDraftPrompt(e.target.value);
          }}
          rows={rows}
          aria-label={t('chat:promptRefinement.editPrompt')}
          data-testid="prompt-refinement-angle-edit-input"
        />
      ) : (
        <p className={styles.cardPrompt}>{displayedPrompt}</p>
      )}
      <div className={styles.cardAction}>
        {isEditing ? (
          <>
            <Button
              type="button"
              variant="tertiary"
              size="small"
              label={t('chat:promptRefinement.cancel')}
              onClick={handleCancelEdit}
            />
            <Button
              type="button"
              variant="secondary"
              size="small"
              label={t('chat:promptRefinement.update')}
              onClick={handleApplyEdit}
              disabled={isUpdateDisabled}
            />
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              size="small"
              label={t('chat:promptRefinement.editPrompt')}
              onClick={handleStartEdit}
            />
            <Button
              type="button"
              variant="primary"
              size="small"
              label={t('chat:promptRefinement.usePrompt')}
              onClick={() => onSelectPrompt(displayedPrompt)}
              data-testid="prompt-refinement-use-prompt-button"
            />
          </>
        )}
      </div>
    </Card>
  );
};
